/**
 * "Kept at" — the one word this app uses for a storage location's temperature
 * (never "Held at", "Storage profile" or "Condition"). Pure so it is covered:
 * this repo's vitest only collects `src/**\/*.test.ts`.
 *
 * "Kept at" picks an inventory Storage Profile, not a raw condition — the same
 * profile a material points at for what it requires — so a menu offers
 * `KeptAtOption`s built from `ApiProfile` rather than the condition enum
 * directly. `resolveKeptAt` is the other half: reading what a node (and its
 * ancestors) already resolve to, including the legacy case where a condition
 * was set directly rather than through a profile.
 */

import type { ApiProfile } from '@/types/inventory'
import type { ApiStorageNode, StorageCondition, StorageConditionMeta, TemperatureFamily } from '@/types/storage'
import { profileCondition } from '@/lib/inventoryProfiles'

/** One profile offered in a "Kept at" menu. */
export type KeptAtOption = {
  profileId: string
  name: string
  condition: StorageCondition
  temperature: string
  family: TemperatureFamily | null
  label: string
}

/** "<temperature> · <name>", or just the name when there is no temperature to show. */
export function keptAtLabel(temperature: string, name: string): string {
  return temperature ? `${temperature} · ${name}` : name
}

/**
 * A trailing temperature a profile's own name repeats, e.g. "-150 C",
 * "-150 to -196 C" or "-20 °C" at the end of the string, after at least one
 * other word. Requiring a leading space is what keeps a name that is NOTHING
 * but a temperature (`-80 C`) from matching — there is no word before it to
 * leave behind, so `profileDisplayName` falls back to the original there.
 */
const TRAILING_TEMPERATURE = /\s+-?\d+(?:\.\d+)?(?:\s*(?:to|-)\s*-?\d+(?:\.\d+)?)?\s*°?\s*[CF]\s*$/i

/**
 * A Kept-at option's display name, with a trailing temperature stripped when
 * the profile's own name already repeats it — e.g. "Cryogenic -150 C" next to
 * a "≤ −150 °C" temperature column reads as "≤ −150 °C Cryogenic -150 C"
 * otherwise. Only what is DISPLAYED changes; the stored name is untouched.
 *
 * Falls back to the original name whenever stripping would leave nothing —
 * a name that is nothing but a temperature still needs something to show.
 */
export function profileDisplayName(name: string): string {
  const stripped = name.replace(TRAILING_TEMPERATURE, '').trim()
  return stripped || name
}

/**
 * Profiles a "Kept at" menu may offer, in the order they should list: by the
 * condition's own `order`, then by profile name.
 *
 * Only profiles naming a condition that is both persistent and active are
 * offered — a controlled-rate freezer is a ramp nothing is kept at, and a
 * retired condition is not a choice for new stock — and a profile whose
 * condition is missing from `conditions` entirely is excluded too, rather than
 * rendered with nothing to show.
 */
export function keptAtOptions(profiles: ApiProfile[], conditions: StorageConditionMeta[]): KeptAtOption[] {
  const byName = new Map(conditions.map((c) => [c.name, c]))
  return profiles
    .map((profile) => {
      const condition = profileCondition(profile)
      const meta = condition != null ? byName.get(condition) : undefined
      return meta && meta.persistent && meta.active
        ? {
            profileId: profile.id,
            name: profile.name,
            condition: meta.name,
            temperature: meta.temperature,
            family: meta.family,
            label: keptAtLabel(meta.temperature, profile.name),
          }
        : null
    })
    .filter((o): o is KeptAtOption => o !== null)
    .sort((a, b) => {
      const orderA = byName.get(a.condition)?.order ?? 0
      const orderB = byName.get(b.condition)?.order ?? 0
      return orderA - orderB || a.name.localeCompare(b.name)
    })
}

/** Where a node's "Kept at" value comes from. */
export type ResolvedKeptAt = {
  source: 'own' | 'legacy' | 'inherited' | 'none'
  condition: StorageCondition | null
  profileId: string | null
  fromName: string | null
}

/**
 * Resolves "Kept at" for the last node of `chain` (root→node).
 *
 * The node itself decides `'own'` vs `'legacy'`: a `storageProfileId` means the
 * condition comes from a profile, while a bare `providesCondition` is a value
 * set directly — by the seed, an import, or the API — before profiles carried
 * it. Only once the node itself names nothing does an ancestor's condition
 * count, nearest first, whether or not that ancestor has a profile — picking a
 * profile is only ever an edit to the node itself.
 */
export function resolveKeptAt(chain: ApiStorageNode[]): ResolvedKeptAt {
  const node = chain[chain.length - 1]

  if (node?.providesCondition) {
    return node.storageProfileId
      ? { source: 'own', condition: node.providesCondition, profileId: node.storageProfileId, fromName: null }
      : { source: 'legacy', condition: node.providesCondition, profileId: null, fromName: null }
  }

  for (let i = chain.length - 2; i >= 0; i--) {
    const ancestor = chain[i]
    if (ancestor.providesCondition) {
      return {
        source: 'inherited',
        condition: ancestor.providesCondition,
        profileId: null,
        fromName: ancestor.name ?? ancestor.code,
      }
    }
  }

  return { source: 'none', condition: null, profileId: null, fromName: null }
}

/**
 * What the last node of `chain` (root→node) would be kept at if it took its
 * value from above — the "Same as <parent>: <temperature>" line beside the
 * inherit switch. The node's own value is ignored: this answers "what would I
 * get", not "what do I have". Named after the node that actually sets it.
 *
 * Null for a unit, which has nothing above it.
 */
export function inheritedKeptAt(
  chain: ApiStorageNode[],
): { condition: StorageCondition | null; fromName: string } | null {
  if (chain.length < 2) return null
  const above = chain.slice(0, -1)
  const parent = above[above.length - 1]
  const resolved = resolveKeptAt(above)
  return {
    condition: resolved.condition,
    fromName: resolved.source === 'inherited' && resolved.fromName ? resolved.fromName : (parent.name ?? parent.code),
  }
}
